import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getConversation, 
  sendMessage, 
  markMessageAsRead, 
  editMessage,
  deleteMessage,
  ConversationMessage 
} from '../services/messages';
import { uploadToSupabase } from '../services/supabase';
import { validateFileSize, MAX_FILE_SIZE_IMAGES, MAX_FILE_SIZE_DOCUMENTS } from '../utils/fileValidation';
import { useFileError } from '../contexts/FileErrorContext';
import { useTimeout } from '../contexts/TimeoutContext';
import { handleRateLimitError } from '../utils/rateLimitHandler';
import { handleUploadError } from '../utils/uploadErrorHandler';
import Modal from './Modal';
import './ChatView.css';
interface ChatViewProps {
  otherUserId: number;
  onBack: () => void;
  onMessageSent?: () => void;
  currentUserRole?: string;
  hideBackButton?: boolean;
  /** Si true, el mensaje se envía como soporte (admin) para que el usuario final no note cambio */
  sendAsSupport?: boolean;
}
const ChatView: React.FC<ChatViewProps> = ({ otherUserId, onBack, onMessageSent, currentUserRole, hideBackButton = false, sendAsSupport = false }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [otherUser, setOtherUser] = useState<{ 
    id: number; 
    email: string; 
    role: string; 
    name?: string;
    firstName?: string | null;
    lastName?: string | null;
    organizationName?: string | null;
    position?: string | null;
    profileId?: number | null;
  } | null>(null);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingMessageDimensions, setEditingMessageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [replyToMessageId, setReplyToMessageId] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { showFileSizeError } = useFileError();
  const { showTimeout } = useTimeout();
  // Estado para rastrear si es la primera carga
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    // Resetear isFirstLoad cuando cambia el usuario
    setIsFirstLoad(true);
    loadConversation();
    // Recargar conversación cada 5 segundos para ver nuevos mensajes
    const interval = setInterval(loadConversation, 5000);
    return () => clearInterval(interval);
  }, [otherUserId]);

  // Solo hacer scroll automático si el usuario está al final del chat
  useEffect(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100; // 100px de margen
      // SIEMPRE hacer scroll en la primera carga, después solo si estaba al final
      if (isFirstLoad || isAtBottom || messages.length === 0) {
        scrollToBottom();
        if (isFirstLoad && messages.length > 0) {
          setIsFirstLoad(false);
        }
      }
    }
  }, [messages, isFirstLoad]);
  // Ajustar el textarea de edición para que tenga las mismas dimensiones que el mensaje original
  useEffect(() => {
    if (editingMessageId && editTextareaRef.current && editingMessageDimensions) {
      // Usar requestAnimationFrame para asegurar que el DOM esté completamente renderizado
      requestAnimationFrame(() => {
        const textarea = editTextareaRef.current;
        if (!textarea) return;
        const messageContent = textarea.closest('.chat-message-content') as HTMLElement;
        if (!messageContent) return;
        // Obtener las dimensiones originales capturadas
        const originalWidth = editingMessageDimensions.width;
        const originalHeight = editingMessageDimensions.height;
        // Obtener el ancho máximo disponible del contenedor padre
        const messageContentWidth = messageContent.clientWidth;
        const messageContentPadding = 24; // 12px izquierdo + 12px derecho
        const maxAvailableWidth = messageContentWidth - messageContentPadding;
        // Obtener el ancho máximo del contenedor del chat (70% del viewport)
        const chatContainer = messagesContainerRef.current;
        const chatMaxWidth = chatContainer 
          ? chatContainer.clientWidth * 0.7 
          : window.innerWidth * 0.7;
        // Usar el ancho del mensaje original, pero limitarlo para evitar desbordes
        const targetWidth = Math.min(originalWidth, maxAvailableWidth, chatMaxWidth);
        const finalWidth = Math.max(targetWidth, 200); // mínimo 200px
        // Aplicar el ancho (el textarea tiene padding de 8px a cada lado = 16px total)
        const textareaPadding = 16;
        const textareaWidth = finalWidth + textareaPadding;
        const textareaMaxWidth = maxAvailableWidth + textareaPadding;
        textarea.style.width = `${textareaWidth}px`;
        textarea.style.maxWidth = `${textareaMaxWidth}px`;
        textarea.style.minWidth = '216px'; // 200px + 16px padding
        textarea.style.flexShrink = '0';
        textarea.style.flexGrow = '0';
        textarea.style.overflowX = 'hidden';
        // Ajustar la altura
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const finalHeight = Math.max(originalHeight, scrollHeight);
        textarea.style.height = `${finalHeight}px`;
        textarea.style.overflowY = scrollHeight > originalHeight ? 'auto' : 'hidden';
        // Forzar el word-wrap para evitar desbordes
        textarea.style.wordWrap = 'break-word';
        textarea.style.overflowWrap = 'break-word';
        textarea.style.wordBreak = 'break-word';
      });
    }
  }, [editingMessageId, editingMessageDimensions]);
  const loadConversation = async () => {
    try {
      setError(null);
      // Guardar posición del scroll antes de actualizar
      const container = messagesContainerRef.current;
      const previousScrollTop = container?.scrollTop || 0;
      const previousScrollHeight = container?.scrollHeight || 0;
      const wasAtBottom = container ? 
        (container.scrollHeight - container.scrollTop <= container.clientHeight + 100) : true;
      const response = await getConversation(otherUserId);
      setOtherUser(response.otherUser);
      // Usar setTimeout para restaurar scroll después de que React actualice el DOM
      setTimeout(() => {
        if (container) {
          if (wasAtBottom) {
            // Si estaba al final, ir al final
            scrollToBottom();
          } else {
            // Si no estaba al final, mantener la posición relativa
            const newScrollHeight = container.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;
            container.scrollTop = previousScrollTop + scrollDifference;
          }
        }
      }, 0);
      setMessages(response.messages);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Error al cargar conversación');
      setLoading(false);
    }
  };
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Limpiar errores previos
    setUploadError(null);
    // Determinar si es imagen o documento
    const isImage = file.type.startsWith('image/');
    const maxSize = isImage ? MAX_FILE_SIZE_IMAGES : MAX_FILE_SIZE_DOCUMENTS;
    const validation = validateFileSize(file, maxSize, isImage ? 'image' : 'document');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'El archivo excede el tamaño máximo permitido.');
      return;
    }
    setSelectedFile(file);
    // Crear preview si es imagen
    if (isImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };
  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleSendMessage = async () => {
    const hasText = messageText.trim();
    const hasFile = selectedFile !== null;
    if ((!hasText && !hasFile) || sending || uploadingFile) {
      return;
    }
    try {
      setSending(true);
      setUploadingFile(true);
      setUploadError(null);
      let attachmentUrl: string | undefined;
      let attachmentFilename: string | undefined;
      let attachmentType: string | undefined;
      // Subir archivo si hay uno seleccionado
      if (selectedFile) {
        try {
          const isImage = selectedFile.type.startsWith('image/');
          attachmentUrl = await uploadToSupabase(selectedFile, {
            folder: 'perfil-institucional/messages',
            onProgress: (progress) => {
              // Opcional: mostrar progreso
            }
          });
          
          // Log para debugging
          
          attachmentFilename = selectedFile.name;
          attachmentType = isImage ? 'image' : 'document';
        } catch (err: any) {
          // Verificar si es un error de rate limiting (429)
          if (handleRateLimitError(err)) {
            setUploadingFile(false);
            setSending(false);
            return;
          }
          
          // Verificar si es un error de formato o tamaño del backend
          if (handleUploadError(err, showFileSizeError)) {
            setUploadingFile(false);
            setSending(false);
            return;
          }
          
          // Verificar si es un error de timeout
          if (err?.isTimeout) {
            showTimeout(err.operationName || 'la subida del archivo');
            setUploadingFile(false);
            setSending(false);
            return;
          }
          
          // Manejar errores 403 (permisos)
          let errorMessage = err.message || 'Error desconocido al subir archivo';
          if (err.status === 403 || err.statusCode === 403 || err.message?.includes('403')) {
            errorMessage = 'Error de permisos al subir archivo. Por favor, contacta al administrador del sistema.';
          }
          setUploadError(errorMessage);
          setUploadingFile(false);
          setSending(false);
          return;
        }
        setUploadingFile(false);
      }
      // Construir el objeto de mensaje
      const messageData: any = {
        toUserId: otherUserId,
      };
      // Incluir mensaje si hay texto
      if (hasText) {
        messageData.message = messageText.trim();
      }
      // Incluir archivo si hay uno
      if (attachmentUrl) {
        messageData.attachmentUrl = attachmentUrl;
        messageData.attachmentFilename = attachmentFilename;
        messageData.attachmentType = attachmentType;
      }
      // Incluir replyToMessageId si hay un reply
      if (replyToMessageId) {
        messageData.replyToMessageId = replyToMessageId;
      }
      if (sendAsSupport) {
        messageData.sendAsSupport = true;
      }
      await sendMessage(messageData);
      setMessageText('');
      setSelectedFile(null);
      setFilePreview(null);
      setUploadError(null);
      setReplyToMessageId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Resetear altura del textarea
      const textarea = document.querySelector('.chat-input') as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.height = 'auto';
      }
      await loadConversation();
      // Después de enviar un mensaje, siempre ir al final
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Error desconocido al enviar mensaje';
      setUploadError(errorMessage);
      setError(errorMessage);
    } finally {
      setSending(false);
      setUploadingFile(false);
    }
  };
  const handleEditMessage = async (messageId: number, newText: string) => {
    if (!newText.trim()) return;
    try {
      await editMessage(messageId, newText.trim());
      setEditingMessageId(null);
      setEditingMessageDimensions(null);
      await loadConversation();
    } catch (err: any) {
      // Error silencioso - no mostrar diálogo al usuario
    }
  };
  const handleDeleteMessage = (messageId: number) => {
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
  };
  const confirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      await deleteMessage(messageToDelete);
      setShowDeleteModal(false);
      setMessageToDelete(null);
      await loadConversation();
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (err: any) {
      // Error silencioso - no mostrar diálogo al usuario
      setShowDeleteModal(false);
      setMessageToDelete(null);
    }
  };
  const handleTyping = () => {
    setIsTyping(true);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
    setTypingTimeout(timeout);
  };
  const getReplyMessage = (replyToId: number | undefined): ConversationMessage | null => {
    if (!replyToId) return null;
    return messages.find(m => m.id === replyToId) || null;
  };
  const handleDownloadFile = async (messageId: number, filename: string) => {
    try {
      // Usar el endpoint del backend para descargar el archivo
      const { downloadMessageAttachment } = await import('../services/messages');
      const blob = await downloadMessageAttachment(messageId, filename);
      // Crear un objeto URL temporal
      const blobUrl = window.URL.createObjectURL(blob);
      // Crear un enlace temporal y hacer clic para descargar
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      // Forzar el clic
      link.click();
      // Esperar un poco antes de limpiar para asegurar que la descarga se inicie
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 200);
    } catch (error) {
      alert('No se pudo descargar el archivo. Por favor, intenta nuevamente.');
    }
  };
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  // Función helper para convertir fecha de BD a GMT-3 (sumar 3 horas)
  const parseGMT3Date = (dateString: string): Date => {
    // La BD guarda en UTC (o timezone que está 3 horas atrás)
    // Para mostrar en GMT-3 (Argentina), hay que SUMAR 3 horas
    // Ejemplo: BD tiene 15:00 → mostrar 18:00
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      // Si no tiene timezone, asumir UTC y sumar 3 horas para GMT-3
      // Formato esperado: "2024-01-29 15:00:00" o "2024-01-29T15:00:00"
      const dateTime = dateString.replace(' ', 'T');
      // Crear Date interpretando como UTC
      const utcDate = new Date(dateTime + 'Z');
      // SUMAR 3 horas para convertir a GMT-3 (Argentina)
      const gmt3Date = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000));
      return gmt3Date;
    }
    // Si ya tiene timezone, convertir a Date y sumar 3 horas
    const date = new Date(dateString);
    return new Date(date.getTime() + (3 * 60 * 60 * 1000));
  };

  const formatMessageTime = (dateString: string) => {
    const date = parseGMT3Date(dateString);
    return date.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Argentina/Buenos_Aires' // GMT-3
    });
  };
  const getFileIcon = (type?: string) => {
    if (!type) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      );
    }
    if (type === 'image') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      );
    }
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
      </svg>
    );
  };
  // Función helper para extraer solo el primer nombre
  const getFirstName = (fullName?: string): string => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };
  if (loading) {
    return (
      <div className="chat-view">
        <div className="chat-loading">Cargando conversación...</div>
      </div>
    );
  }
  if (error || !otherUser) {
    return (
      <div className="chat-view">
        <div className="chat-error">{error || 'Error al cargar conversación'}</div>
        {!hideBackButton && (
          <button type="button" className="chat-back-button" onClick={onBack}>
            ← Volver
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="chat-view">
      <div className="chat-header">
        {!hideBackButton && (
          <button
            type="button"
            className="chat-back-button"
            onClick={onBack}
          >
            {currentUserRole === 'admin' ? '← Volver' : '← Volver al perfil'}
          </button>
        )}
        {/* Botón de volver en mobile solo para admin, con mismo estilo que chat-back-button pero solo icono */}
        {/* Solo se muestra cuando hideBackButton es true (admin en mobile) */}
        {currentUserRole === 'admin' && hideBackButton && (
          <button
            type="button"
            className="chat-back-button-mobile"
            onClick={onBack}
            title="Volver a mensajería"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
        )}
        <div className="chat-header-info">
          {currentUserRole === 'admin' && otherUser.role !== 'admin' ? (
            <>
              <div className="chat-header-name">
                {otherUser.firstName || otherUser.lastName
                  ? `${otherUser.firstName || ''} ${otherUser.lastName || ''}`.trim()
                  : otherUser.email}
              </div>
              <div className="chat-header-details">
                {[
                  otherUser.organizationName,
                  otherUser.position,
                  otherUser.email
                ].filter(Boolean).join(' • ')}
              </div>
            </>
          ) : (
            <span className="chat-header-email">
              {otherUser.role === 'admin'
                ? otherUser.name && otherUser.name !== otherUser.email
                  ? `${getFirstName(otherUser.name)} (Soporte)`
                  : 'Soporte'
                : otherUser.name && otherUser.name !== otherUser.email
                  ? getFirstName(otherUser.name)
                  : otherUser.email}
            </span>
          )}
          {isTyping && (
            <span className="chat-header-role">
              <span className="chat-typing-indicator"> está escribiendo...</span>
            </span>
          )}
        </div>
        {/* Botón para ver usuario en admin panel - solo para admin */}
        {currentUserRole === 'admin' && otherUser.role !== 'admin' && (
          <button
            type="button"
            className="chat-view-user-button"
            onClick={() => {
              // Guardar el userId en sessionStorage para que el AdminPanel lo cargue
              const adminPanelState = sessionStorage.getItem('adminPanelState');
              let state = adminPanelState ? JSON.parse(adminPanelState) : {};
              state.selectedCrmUserId = otherUser.id;
              state.activeMainTab = 'crm';
              sessionStorage.setItem('adminPanelState', JSON.stringify(state));
              // Abrir el admin panel en una nueva pestaña
              const url = `${window.location.origin}/admin`;
              window.open(url, '_blank');
            }}
            title="Ver usuario en panel de administración"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </button>
        )}
      </div>
      <div className="chat-messages" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className="chat-empty">No hay mensajes en esta conversación</div>
        ) : (
          messages.map((message) => {
            const replyTo = getReplyMessage(message.replyToMessageId);
            const isEditing = editingMessageId === message.id;
            // Determinar si el mensaje solo tiene imagen (sin texto)
            const hasOnlyImage = message.attachmentUrl && 
                                 message.attachmentType === 'image' && 
                                 !message.message;
            return (
              <div
                key={message.id}
                className={`chat-message ${message.isFromCurrentUser ? 'sent' : 'received'} ${hasOnlyImage ? 'image-only' : ''}`}
              >
                <div className={`chat-message-content ${hasOnlyImage ? 'image-only-content' : ''}`}>
                  {replyTo && (
                    <div className="chat-message-reply">
                      <div className="chat-message-reply-line"></div>
                      <div className="chat-message-reply-content">
                        <span className="chat-message-reply-author">
                          {replyTo.isFromCurrentUser 
                            ? 'Tú' 
                            : replyTo.fromUser.role === 'admin'
                              ? (replyTo.fromUser.name && replyTo.fromUser.name !== replyTo.fromUser.email
                                  ? `${getFirstName(replyTo.fromUser.name)} (Soporte)`
                                  : 'Soporte')
                              : (getFirstName(replyTo.fromUser.name) || replyTo.fromUser.email)}
                        </span>
                        <span className="chat-message-reply-text">
                          {replyTo.attachmentUrl 
                            ? `${getFileIcon(replyTo.attachmentType)} ${replyTo.attachmentFilename || 'Archivo adjunto'}`
                            : replyTo.message || 'Mensaje eliminado'}
                        </span>
                      </div>
                    </div>
                  )}
                  {isEditing ? (
                    <>
                      {/* Mostrar archivo/imagen arriba del contenedor de edición */}
                      {message.attachmentUrl && (
                        <div className="chat-message-attachment">
                          {message.attachmentType === 'image' ? (
                            <img 
                              src={message.attachmentUrl} 
                              alt={message.attachmentFilename || 'Imagen adjunta'}
                              className="chat-message-image"
                            />
                          ) : (
                            <div className="chat-message-file">
                              {getFileIcon(message.attachmentType)} 
                              <span className="chat-message-filename">{message.attachmentFilename || 'Archivo adjunto'}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="chat-message-edit">
                        <textarea
                          ref={editTextareaRef}
                          className="chat-message-edit-input"
                          defaultValue={message.message || ''}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleEditMessage(message.id, e.currentTarget.value);
                            }
                            if (e.key === 'Escape') {
                              setEditingMessageId(null);
                              setEditingMessageDimensions(null);
                            }
                          }}
                          onBlur={(e) => {
                            if (e.currentTarget.value.trim() !== message.message) {
                              handleEditMessage(message.id, e.currentTarget.value);
                            } else {
                              setEditingMessageId(null);
                              setEditingMessageDimensions(null);
                            }
                          }}
                        />
                        <div className="chat-message-edit-hint">Enter para guardar, Esc para cancelar</div>
                      </div>
                    </>
                  ) : (
                    <>
                      {message.attachmentUrl && (
                        <div className="chat-message-attachment">
                          {message.attachmentType === 'image' ? (
                            <img 
                              src={message.attachmentUrl} 
                              alt={message.attachmentFilename || 'Imagen adjunta'}
                              className="chat-message-image"
                            />
                          ) : (
                            <div className="chat-message-file">
                              {getFileIcon(message.attachmentType)} 
                              <span className="chat-message-filename">{message.attachmentFilename || 'Archivo adjunto'}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {message.message && (
                        <div className="chat-message-text">{message.message}</div>
                      )}
                    </>
                  )}
                  <div className="chat-message-footer">
                    <div className="chat-message-time">
                      {formatMessageTime(message.createdAt)}
                      {message.edited && <span className="chat-message-edited"> (editado)</span>}
                      {message.isFromCurrentUser && message.delivered && (
                        <span className="chat-message-checkmark">
                          {message.seenAt ? '✓✓' : '✓'}
                        </span>
                      )}
                    </div>
                    {message.isFromCurrentUser && !isEditing && (
                      <div className="chat-message-actions">
                        <button
                          type="button"
                          className="chat-message-action-btn"
                          onClick={(e) => {
                            // Capturar las dimensiones del mensaje original antes de activar la edición
                            const messageContent = (e.currentTarget.closest('.chat-message')?.querySelector('.chat-message-content') as HTMLElement);
                            if (messageContent) {
                              // Usar getBoundingClientRect() para obtener las dimensiones reales renderizadas
                              const rect = messageContent.getBoundingClientRect();
                              // El ancho y alto del contenedor completo, menos el padding (12px a cada lado = 24px total)
                              const padding = 24;
                              const contentWidth = Math.max(rect.width - padding, 200); // mínimo 200px
                              const contentHeight = rect.height;
                              setEditingMessageDimensions({
                                width: contentWidth,
                                height: contentHeight
                              });
                            }
                            setEditingMessageId(message.id);
                          }}
                          title="Editar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="chat-message-action-btn"
                          onClick={() => handleDeleteMessage(message.id)}
                          title="Eliminar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    )}
                    {!message.isFromCurrentUser && (
                      <div className="chat-message-actions">
                        {message.attachmentUrl && (
                          <button
                            type="button"
                            className="chat-message-action-btn"
                            onClick={() => handleDownloadFile(
                              message.id,
                              message.attachmentFilename || (message.attachmentType === 'image' ? 'imagen' : 'archivo')
                            )}
                            title="Descargar"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="7 10 12 15 17 10"></polyline>
                              <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          className="chat-message-action-btn"
                          onClick={() => setReplyToMessageId(message.id)}
                          title="Responder"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 10 4 15 9 20"></polyline>
                            <path d="M20 4v7a4 4 0 0 1-4 4H4"></path>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-input-container">
        {replyToMessageId && (() => {
          const replyMessage = getReplyMessage(replyToMessageId);
          return replyMessage && (
            <div className="chat-reply-preview">
              <div className="chat-reply-preview-content">
                <span className="chat-reply-preview-label">Respondiendo a:</span>
                {replyMessage.attachmentUrl ? (
                  replyMessage.attachmentType === 'image' ? (
                    <div className="chat-reply-preview-attachment">
                      <img 
                        src={replyMessage.attachmentUrl} 
                        alt={replyMessage.attachmentFilename || 'Imagen adjunta'}
                        className="chat-reply-preview-image"
                      />
                      <span className="chat-reply-preview-filename">
                        {replyMessage.attachmentFilename || 'Imagen adjunta'}
                      </span>
                    </div>
                  ) : (
                    <div className="chat-reply-preview-attachment">
                      {getFileIcon(replyMessage.attachmentType)}
                      <span className="chat-reply-preview-filename">
                        {replyMessage.attachmentFilename || 'Archivo adjunto'}
                      </span>
                    </div>
                  )
                ) : (
                  <span className="chat-reply-preview-text">
                    {replyMessage.message || 'Mensaje eliminado'}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="chat-reply-preview-close"
                onClick={() => setReplyToMessageId(null)}
              >
                ×
              </button>
            </div>
          );
        })()}
        {filePreview && (
          <div className="chat-file-preview">
            <img src={filePreview} alt="Preview" className="chat-file-preview-image" />
            <button
              type="button"
              className="chat-file-preview-remove"
              onClick={removeFile}
            >
              ×
            </button>
          </div>
        )}
        {selectedFile && !filePreview && (
          <div className="chat-file-preview">
            <span className="chat-file-preview-name">
              {getFileIcon(selectedFile.type.startsWith('image/') ? 'image' : 'document')} {selectedFile.name}
            </span>
            <button
              type="button"
              className="chat-file-preview-remove"
              onClick={removeFile}
            >
              ×
            </button>
          </div>
        )}
        {uploadError && (
          <div className="chat-upload-error">
            <span className="chat-upload-error-text">⚠️ {uploadError}</span>
            <button
              type="button"
              className="chat-upload-error-close"
              onClick={() => setUploadError(null)}
              title="Cerrar"
            >
              ×
            </button>
          </div>
        )}
        <div className="chat-input-wrapper">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="chat-file-input"
          />
          <button
            type="button"
            className="chat-attach-button"
            onClick={() => fileInputRef.current?.click()}
            title="Adjuntar archivo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
          </button>
          <textarea
            value={messageText}
            onChange={(e) => {
              setMessageText(e.target.value);
              handleTyping();
              // Auto-expandir textarea
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            onPaste={async (e) => {
              // Detectar si se pegó una imagen desde el portapapeles
              const items = e.clipboardData.items;
              for (let i = 0; i < items.length; i++) {
                const item = items[i];
                // Verificar si es una imagen
                if (item.type.indexOf('image') !== -1) {
                  e.preventDefault(); // Prevenir que se pegue el texto de la imagen
                  const blob = item.getAsFile();
                  if (blob) {
                    // Crear un File desde el Blob con un nombre por defecto
                    const timestamp = new Date().getTime();
                    const fileExtension = blob.type.split('/')[1] || 'png';
                    const fileName = `imagen-${timestamp}.${fileExtension}`;
                    const file = new File([blob], fileName, { type: blob.type });
                    // Validar tamaño del archivo
                    const isImage = file.type.startsWith('image/');
                    const maxSize = isImage ? MAX_FILE_SIZE_IMAGES : MAX_FILE_SIZE_DOCUMENTS;
                    const validation = validateFileSize(file, maxSize);
                    if (!validation.valid) {
                      showFileSizeError(validation.error || 'El archivo es demasiado grande');
                      return;
                    }
                    // Establecer el archivo como seleccionado (esto activará la preview y permitirá enviarlo)
                    setSelectedFile(file);
                    // Si es una imagen, crear preview
                    if (isImage) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setFilePreview(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                    // Limpiar cualquier error previo
                    setUploadError(null);
                  }
                  break; // Solo procesar la primera imagen encontrada
                }
              }
            }}
            className="chat-input"
            placeholder="Escribe un mensaje..."
            rows={1}
          />
          <button
            type="button"
            className="chat-send-button"
            onClick={handleSendMessage}
            disabled={(!messageText.trim() && !selectedFile) || sending || uploadingFile}
          >
            {sending || uploadingFile ? '...' : '→'}
          </button>
        </div>
      </div>
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMessageToDelete(null);
        }}
        onConfirm={confirmDeleteMessage}
        confirmText="Aceptar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de que deseas eliminar este mensaje?</p>
      </Modal>
    </div>
  );
};
export default ChatView;
